import Store from 'repatch';
import State from '../core/State';
import SalesWebApi from '../service/persistent/SalesWebApi';
import SummariesWebApi from '../service/persistent/SummariesWebApi';
import VisitorWebApi from '../service/persistent/VisitorWebApi';
import { startFetchingSales, setSales, finishFetchingSales, disposeSales } from '../thunk/sales';
import {
  startFetchingSalesByVisitor,
  setSalesByVisitor,
  finishFetchingSalesByVisitor,
  disposeSalesByVisitor,
} from '../thunk/salesByVisitor';
import {
  startFetchingSalesBySegments,
  setSalesBySegments,
  finishFetchingSalesBySegments,
  disposeSalesBySegments,
} from '../thunk/salesBySegments';
import {
  startFetchingBusinessSummary,
  setBusinessSummary,
  finishFetchingBusinessSummary,
  disposeBusinessSummary,
} from '../thunk/businessSummary';
import {
  startFetchingUsersSummary,
  setUsersSummary,
  finishFetchingUsersSummary,
  disposeUsersSummary,
} from '../thunk/usersSummary';
import { startFetchingVisitor, finishFetchingVisitor, setVisitor, disposeVisitor } from '../thunk/visitor';
import {
  disposeVisitorsBySession,
  startFetchingVisitorsBySession,
  setVisitorsBySession,
  finishFetchingVisitorsBySession,
} from '../thunk/visitorsBySession';
import {
  disposeVisitorsBySalesRate,
  startFetchingVisitorsBySalesRate,
  setVisitorsBySalesRate,
  finishFetchingVisitorsBySalesRate,
} from '../thunk/purchaseRates';
import {
  disposeVisitorsByPv,
  startFetchingVisitorsByPv,
  setVisitorsByPv,
  finishFetchingVisitorsByPv,
} from '../thunk/visitorsByPv';
import {
  startFetchingVisitorsBySegments,
  setVisitorsBySegments,
  finishFetchingVisitorsBySegments,
  disposeVisitorsBySegments,
} from '../thunk/visitorsBySegments';
import {
  startFetchingPurchasers,
  setPurchasers,
  finishFetchingPurchasers,
  disposePurchasers,
} from '../thunk/purchasers';
import SegmentWebApi from '../service/persistent/SegmentWebApi';
import { IInterval, OrderDirection, SalesByProductOrder } from '@kaizenplatform/intelligence-entity';
import { CURRENT_PERIOD, PREVIOUS_PERIOD } from '../view/constant/periodLabelOfSummary';
import {
  startFetchingSalesByCategorySummaries,
  finishFetchingSalesByCategorySummaries,
  setSalesByCategorySummaries,
  disposeSalesByCategorySummaries,
} from '../thunk/salesByCategorySummaries';
import {
  startFetchingSalesByCategory,
  setSalesByCategory,
  disposeSalesByCategory,
  finishFetchingSalesByCategory,
} from '../thunk/salesByCategory';
import { startFetchingSalesByProduct, setSalesByProduct, finishFetchingSalesByProduct } from '../thunk/salesByProduct';
import Pagination from '../types/Pagination';
import SalesByProductOrderTarget from '../types/SalesByProductOrderTarget';
import ApiErrorHandler from '../service/ApiErrorHandler';

class CreateChartsUsecase {
  private store: Store<State>;
  private salesWebApi: SalesWebApi;
  private segmentWebApi: SegmentWebApi;
  private summariesWebApi: SummariesWebApi;
  private visitorWebApi: VisitorWebApi;
  private apiErrorHandler: ApiErrorHandler;

  constructor({
    store,
    salesWebApi,
    segmentWebApi,
    summariesWebApi,
    visitorWebApi,
    apiErrorHandler,
  }: {
    store: Store<State>;
    salesWebApi: SalesWebApi;
    segmentWebApi: SegmentWebApi;
    summariesWebApi: SummariesWebApi;
    visitorWebApi: VisitorWebApi;
    apiErrorHandler: ApiErrorHandler;
  }) {
    this.store = store;
    this.salesWebApi = salesWebApi;
    this.segmentWebApi = segmentWebApi;
    this.summariesWebApi = summariesWebApi;
    this.visitorWebApi = visitorWebApi;
    this.apiErrorHandler = apiErrorHandler;
  }

  public prepareBusinessCharts = (): void => {
    this.getBusinessSummary();
    this.getSales();
    this.getVisitors();
    this.getSalesByVisitor();
    this.getPurchasers();
  };

  public prepareUsersCharts = (): void => {
    this.getUsersSummary();
    this.getVisitors();
    this.getVisitorsBySalesRate();
    this.getVisitorsBySession();
    this.getVisitorsByPv();
    this.getSalesBySegments();
    this.getVisitorsBySegments();
  };

  public prepareProductCharts = (): void => {
    this.getBusinessSummary();
    this.getSalesBySegments();
    this.getSalesByCategorySummaries();
    this.getSalesByCategory();
    this.getSalesByProduct('initialization', 'desc', 'value');
  };

  public onClickNextPage = (orderDirection: OrderDirection, orderBy: SalesByProductOrderTarget): void => {
    this.getSalesByProduct('next', orderDirection, orderBy);
  };

  public onClickPreviousPage = (orderDirection: OrderDirection, orderBy: SalesByProductOrderTarget): void => {
    this.getSalesByProduct('back', orderDirection, orderBy);
  };

  public onClickSorting = (orderDirection: OrderDirection, orderBy: SalesByProductOrderTarget): void => {
    this.getSalesByProduct('initialization', orderDirection, orderBy);
  };

  private getPeriods = () => {
    const state = this.store.getState();
    const isIncludePrevious = state.globalChartSetting.isIncludePrevious;
    const { from, to } = state.globalChartSetting.period;
    const { from: subFrom, to: subTo } = state.globalChartSetting.subPeriod;

    const mainPeriod = {
      label: `${from.toFormat('yyyy LL dd')} - ${to.toFormat('yyyy LL dd')}`,
      from: from.toISO(),
      to: to.toISO(),
    } as IInterval;

    const subPeriod =
      isIncludePrevious && state.globalChartSetting.subPeriod
        ? ({
            label: `${subFrom.toFormat('yyyy LL dd')} - ${subTo.toFormat('yyyy LL dd')}`,
            from: subFrom.toISO(),
            to: subTo.toISO(),
          } as IInterval)
        : null;

    return subPeriod ? [mainPeriod, subPeriod] : [mainPeriod];
  };

  private getBusinessSummary = async (): Promise<void> => {
    this.store.dispatch(startFetchingBusinessSummary());
    const state = this.store.getState();
    const organizationId = state.lastAccessedOrganization.item!.id;
    const current = state.globalChartSetting.period.from;
    const previous = state.globalChartSetting.subPeriod.from;

    const periods = [
      {
        from: current.startOf('month').toISO(),
        to: current.endOf('month').toISO(),
        label: CURRENT_PERIOD,
      },
      {
        from: previous.startOf('month').toISO(),
        to: previous.endOf('month').toISO(),
        label: PREVIOUS_PERIOD,
      },
    ];

    try {
      const businessSummary = await this.summariesWebApi.businessSummaries(organizationId, periods);

      // const businessSummary = await this.summariesWebApi.summariesStub();
      this.store.dispatch(setBusinessSummary(businessSummary));
    } catch (error) {
      this.apiErrorHandler.handle(error);
    } finally {
      this.store.dispatch(finishFetchingBusinessSummary());
    }
  };

  private getUsersSummary = async (): Promise<void> => {
    this.store.dispatch(startFetchingUsersSummary());
    const state = this.store.getState();
    const organizationId = state.lastAccessedOrganization.item!.id;
    const current = state.globalChartSetting.period.from;
    const previous = state.globalChartSetting.subPeriod.from;

    const periods = [
      {
        from: current.startOf('month').toISO(),
        to: current.endOf('month').toISO(),
        label: CURRENT_PERIOD,
      },
      {
        from: previous.startOf('month').toISO(),
        to: previous.endOf('month').toISO(),
        label: PREVIOUS_PERIOD,
      },
    ];

    try {
      const usersSummary = await this.summariesWebApi.usersSummaries(organizationId, periods);
      // const usersSummary = await this.summariesWebApi.summariesStub();
      this.store.dispatch(setUsersSummary(usersSummary));
    } catch (error) {
      this.apiErrorHandler.handle(error);
    } finally {
      this.store.dispatch(finishFetchingUsersSummary());
    }
  };

  private getSales = async (): Promise<void> => {
    const state = this.store.getState();
    const organizationId = state.lastAccessedOrganization.item!.id;

    this.store.dispatch(startFetchingSales());

    try {
      const sales = await this.salesWebApi.sales(organizationId, this.getPeriods());
      // const sales = await this.salesWebApi.salesStub();

      this.store.dispatch(setSales(sales));
    } catch (error) {
      this.apiErrorHandler.handle(error);
    } finally {
      this.store.dispatch(finishFetchingSales());
    }
  };

  private getSalesByVisitor = async (): Promise<void> => {
    const state = this.store.getState();
    const organizationId = state.lastAccessedOrganization.item!.id;
    // const { from, to } = state.globalChartSetting.period;

    this.store.dispatch(startFetchingSalesByVisitor());

    try {
      const salesByVisitor = await this.salesWebApi.salesByVisitor(organizationId, this.getPeriods());
      // const salesByVisitor = await this.salesWebApi.salesByVisitorStub();
      this.store.dispatch(setSalesByVisitor(salesByVisitor));
    } catch (error) {
      this.apiErrorHandler.handle(error);
    } finally {
      this.store.dispatch(finishFetchingSalesByVisitor());
    }
  };

  private getVisitors = async (): Promise<void> => {
    const state = this.store.getState();
    const organizationId = state.lastAccessedOrganization.item!.id;
    // const { from, to } = state.globalChartSetting.period;

    this.store.dispatch(startFetchingVisitor());

    try {
      const visitors = await this.visitorWebApi.visitors(organizationId, this.getPeriods());
      // const visitors = await this.visitorWebApi.visitorsStub();
      this.store.dispatch(setVisitor(visitors));
    } catch (error) {
      this.apiErrorHandler.handle(error);
    } finally {
      this.store.dispatch(finishFetchingVisitor());
    }
  };

  private getSalesByCategorySummaries = async (): Promise<void> => {
    const state = this.store.getState();
    const organizationId = state.lastAccessedOrganization.item!.id;

    const current = state.globalChartSetting.period.from;
    const previous = state.globalChartSetting.subPeriod.from;

    const periods = [
      {
        from: current.startOf('month').toISO(),
        to: current.endOf('month').toISO(),
        label: CURRENT_PERIOD,
      },
      {
        from: previous.startOf('month').toISO(),
        to: previous.endOf('month').toISO(),
        label: PREVIOUS_PERIOD,
      },
    ];

    this.store.dispatch(startFetchingSalesByCategorySummaries());

    try {
      const summaries = await this.salesWebApi.salesByCategorySummaries(organizationId, periods);
      // const summaries = await this.salesWebApi.salesByCategorySummaryStub();
      this.store.dispatch(setSalesByCategorySummaries(summaries));
    } catch (error) {
      this.apiErrorHandler.handle(error);
    } finally {
      this.store.dispatch(finishFetchingSalesByCategorySummaries());
    }
  };

  private getSalesByCategory = async (): Promise<void> => {
    const state = this.store.getState();
    const organizationId = state.lastAccessedOrganization.item!.id;

    const current = state.globalChartSetting.period.from;
    const previous = state.globalChartSetting.subPeriod.from;

    const periods = [
      {
        from: current.startOf('month').toISO(),
        to: current.endOf('month').toISO(),
        label: CURRENT_PERIOD,
      },
      {
        from: previous.startOf('month').toISO(),
        to: previous.endOf('month').toISO(),
        label: PREVIOUS_PERIOD,
      },
    ];

    this.store.dispatch(startFetchingSalesByCategory());

    try {
      const sales = await this.salesWebApi.salesByCategory(organizationId, periods);
      // const sales = await this.salesWebApi.salesByCategoryStub();
      this.store.dispatch(setSalesByCategory(sales));
    } catch (error) {
      this.apiErrorHandler.handle(error);
    } finally {
      this.store.dispatch(finishFetchingSalesByCategory());
    }
  };

  private getSalesByProduct = async (
    pagination: Pagination,
    orderDirection: OrderDirection,
    orderBy: SalesByProductOrderTarget,
  ): Promise<void> => {
    const state = this.store.getState();
    const organizationId = state.lastAccessedOrganization.item!.id;

    const current = state.globalChartSetting.period.from;

    const before = pagination == 'initialization' ? null : state.salesByProduct.data?.pageInfo.startCursor;
    const after = pagination == 'initialization' ? null : state.salesByProduct.data?.pageInfo.endCursor;

    const period = {
      from: current.startOf('month').toISO(),
      to: current.endOf('month').toISO(),
      label: CURRENT_PERIOD,
    };

    const order =
      orderBy == 'value' || orderBy == 'quantity'
        ? SalesByProductOrder.fromJSON({
            target: orderBy,
            direction: orderDirection,
          })
        : undefined;

    const first = 10;
    const last = 10;

    this.store.dispatch(startFetchingSalesByProduct());

    try {
      const sales =
        pagination == 'initialization'
          ? await this.salesWebApi.salesByProduct(organizationId, period, first, undefined, undefined, undefined, order)
          : pagination == 'next' && after
          ? await this.salesWebApi.salesByProduct(organizationId, period, first, undefined, after, undefined, order)
          : pagination == 'back' && before
          ? await this.salesWebApi.salesByProduct(organizationId, period, undefined, last, undefined, before, order)
          : await this.salesWebApi.salesByProduct(
              organizationId,
              period,
              first,
              undefined,
              undefined,
              undefined,
              order,
            );
      // const sales = await this.salesWebApi.salesByProductStub();
      this.store.dispatch(setSalesByProduct(sales));
    } catch (error) {
      this.apiErrorHandler.handle(error);
    } finally {
      this.store.dispatch(finishFetchingSalesByProduct());
    }
  };

  // private getVisitorsBySalesRate = async (): Promise<void> => {
  //   const state = this.store.getState();
  //   const organizationId = state.lastAccessedOrganization.item!.id;
  //   const { from, to } = state.globalChartSetting.period;

  //   this.store.dispatch(startFetchingVisitorsBySalesRate());

  //   try {
  //     const visitorsBySalesRate = await this.visitorWebApi.visitorsBySalesRate(organizationId, [
  //       {
  //         label: `${from.toFormat('yyyy LL dd')} - ${to.toFormat('yyyy LL dd')}`,
  //         from: from.toISO(),
  //         to: to.toISO(),
  //       },
  //       {
  //         label: `${from.minus({ month: 1 }).toFormat('yyyy LL dd')} - ${to
  //           .minus({ month: 1 })
  //           .toFormat('yyyy LL dd')}`,
  //         from: from.minus({ month: 1 }).toISO(),
  //         to: to.minus({ month: 1 }).toISO(),
  //       },
  //     ]);
  //     // const visitorsBySalesRate = await this.visitorWebApi.visitorsBySalesRateStub();
  //     this.store.dispatch(setVisitorsBySalesRate(visitorsBySalesRate));
  //   } catch (error) {
  //     this.apiErrorHandler.handle(error);
  //   } finally {
  //     this.store.dispatch(finishFetchingVisitorsBySalesRate());
  //   }
  // };

  // private getVisitorsBySession = async (): Promise<void> => {
  //   const state = this.store.getState();
  //   const organizationId = state.lastAccessedOrganization.item!.id;
  //   const { from, to } = state.globalChartSetting.period;

  //   this.store.dispatch(startFetchingVisitorsBySession());

  //   try {
  //     const visitorsBySession = await this.visitorWebApi.visitorsBySession(organizationId, [
  //       {
  //         label: `${from.toFormat('yyyy LL dd')} - ${to.toFormat('yyyy LL dd')}`,
  //         from: from.toISO(),
  //         to: to.toISO(),
  //       },
  //       {
  //         label: `${from.minus({ month: 1 }).toFormat('yyyy LL dd')} - ${to
  //           .minus({ month: 1 })
  //           .toFormat('yyyy LL dd')}`,
  //         from: from.minus({ month: 1 }).toISO(),
  //         to: to.minus({ month: 1 }).toISO(),
  //       },
  //     ]);
  //     // const visitorsBySession = await this.visitorWebApi.visitorsBySessionStub();
  //     this.store.dispatch(setVisitorsBySession(visitorsBySession));
  //   } catch (error) {
  //     this.apiErrorHandler.handle(error);
  //   } finally {
  //     this.store.dispatch(finishFetchingVisitorsBySession());
  //   }
  // };

  // private getVisitorsByPv = async (): Promise<void> => {
  //   const state = this.store.getState();
  //   const organizationId = state.lastAccessedOrganization.item!.id;
  //   const { from, to } = state.globalChartSetting.period;

  //   this.store.dispatch(startFetchingVisitorsByPv());

  //   try {
  //     const visitorsByPv = await this.visitorWebApi.visitorsByPv(organizationId, [
  //       {
  //         label: `${from.toFormat('yyyy LL dd')} - ${to.toFormat('yyyy LL dd')}`,
  //         from: from.toISO(),
  //         to: to.toISO(),
  //       },
  //       {
  //         label: `${from.minus({ month: 1 }).toFormat('yyyy LL dd')} - ${to
  //           .minus({ month: 1 })
  //           .toFormat('yyyy LL dd')}`,
  //         from: from.minus({ month: 1 }).toISO(),
  //         to: to.minus({ month: 1 }).toISO(),
  //       },
  //     ]);
  //     // const visitorsByPv = await this.visitorWebApi.visitorsByPvStub();
  //     this.store.dispatch(setVisitorsByPv(visitorsByPv));
  //   } catch (error) {
  //     this.apiErrorHandler.handle(error);
  //   } finally {
  //     this.store.dispatch(finishFetchingVisitorsByPv());
  //   }
  // };
  private getPurchasers = async (): Promise<void> => {
    const state = this.store.getState();
    const organizationId = state.lastAccessedOrganization.item!.id;
    // const { from, to } = state.globalChartSetting.period;

    this.store.dispatch(startFetchingPurchasers());

    try {
      const purchasers = await this.visitorWebApi.purchasers(organizationId, this.getPeriods());

      this.store.dispatch(setPurchasers(purchasers));
    } catch (error) {
      this.apiErrorHandler.handle(error);
    } finally {
      this.store.dispatch(finishFetchingPurchasers());
    }
  };

  private getVisitorsBySalesRate = async (): Promise<void> => {
    const state = this.store.getState();
    const organizationId = state.lastAccessedOrganization.item!.id;

    this.store.dispatch(startFetchingVisitorsBySalesRate());

    try {
      const purchaseRates = await this.visitorWebApi.purchaseRates(organizationId, this.getPeriods());
      // const purchaseRates = await this.visitorWebApi.visitorsBySalesRateStub();
      this.store.dispatch(setVisitorsBySalesRate(purchaseRates));
    } catch (error) {
      this.apiErrorHandler.handle(error);
    } finally {
      this.store.dispatch(finishFetchingVisitorsBySalesRate());
    }
  };

  private getVisitorsBySession = async (): Promise<void> => {
    const state = this.store.getState();
    const organizationId = state.lastAccessedOrganization.item!.id;

    this.store.dispatch(startFetchingVisitorsBySession());

    try {
      const visitorsBySession = await this.visitorWebApi.visitorsBySession(organizationId, this.getPeriods());
      // const visitorsBySession = await this.visitorWebApi.visitorsBySessionStub();
      this.store.dispatch(setVisitorsBySession(visitorsBySession));
    } catch (error) {
      this.apiErrorHandler.handle(error);
    } finally {
      this.store.dispatch(finishFetchingVisitorsBySession());
    }
  };

  private getVisitorsByPv = async (): Promise<void> => {
    const state = this.store.getState();
    const organizationId = state.lastAccessedOrganization.item!.id;

    this.store.dispatch(startFetchingVisitorsByPv());

    try {
      const visitorsByPv = await this.visitorWebApi.visitorsByPv(organizationId, this.getPeriods());
      // const visitorsByPv = await this.visitorWebApi.visitorsByPvStub();
      this.store.dispatch(setVisitorsByPv(visitorsByPv));
    } catch (error) {
      this.apiErrorHandler.handle(error);
    } finally {
      this.store.dispatch(finishFetchingVisitorsByPv());
    }
  };

  private getSalesBySegments = async (): Promise<void> => {
    const state = this.store.getState();
    const organizationId = state.lastAccessedOrganization.item!.id;
    // const { from, to } = state.globalChartSetting.period;

    this.store.dispatch(startFetchingSalesBySegments());

    try {
      const salesBySegments = await this.segmentWebApi.salesBySegments(organizationId, this.getPeriods());
      // const salesBySegments = await this.segmentWebApi.salesBySegmentsStub();
      this.store.dispatch(setSalesBySegments(salesBySegments));
    } catch (error) {
      this.apiErrorHandler.handle(error);
    } finally {
      this.store.dispatch(finishFetchingSalesBySegments());
    }
  };

  private getVisitorsBySegments = async (): Promise<void> => {
    const state = this.store.getState();
    const organizationId = state.lastAccessedOrganization.item!.id;
    // const { from, to } = state.globalChartSetting.period;

    this.store.dispatch(startFetchingVisitorsBySegments());

    try {
      const visitorsBySegments = await this.segmentWebApi.visitorsBySegments(organizationId, this.getPeriods());
      // const visitorsBySegments = await this.segmentWebApi.visitorsBySegmentsStub();
      this.store.dispatch(setVisitorsBySegments(visitorsBySegments));
    } catch (error) {
      this.apiErrorHandler.handle(error);
    } finally {
      this.store.dispatch(finishFetchingVisitorsBySegments());
    }
  };

  public dispose = (): void => {
    this.store.dispatch(disposeBusinessSummary());
    this.store.dispatch(disposeSales());
    this.store.dispatch(disposeSalesByVisitor());
    this.store.dispatch(disposeUsersSummary());
    this.store.dispatch(disposeVisitor());
    this.store.dispatch(disposeVisitorsByPv());
    this.store.dispatch(disposeVisitorsBySalesRate());
    this.store.dispatch(disposeVisitorsBySession());
    this.store.dispatch(disposeSalesBySegments());
    this.store.dispatch(disposeVisitorsBySegments());
    this.store.dispatch(disposeSalesByCategorySummaries());
    this.store.dispatch(disposeSalesByCategory());
    this.store.dispatch(disposePurchasers());
  };
}

export default CreateChartsUsecase;
